import { itemsQuery } from '@/api/items/itemQueries';
import { Input } from '@/components/common/Input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/common/Table';
import type { Item, ItemStrength } from '@/domain/Item';
import { cn } from '@/lib/cn';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { DashboardTableAccordion } from '../../DashboardTableAccordion';

const ITEMS_PER_PAGE = 15;

const formatPrice = (strength?: ItemStrength): string => {
  if (!strength) return '-';
  const price =
    typeof strength.formatted_price === 'string'
      ? strength.formatted_price
      : String(strength.formatted_price);
  return `€${price}`;
};

const getItemStrengthLabel = (strength?: ItemStrength): string => {
  if (!strength) return '-';
  if (strength.strength.trim()) return strength.strength;
  return `${strength.size}${strength.unit}`;
};

export const ItemsPriceRow = ({
  accordionOpenValue,
}: {
  accordionOpenValue?: string;
}) => {
  const [page, setPage] = useState(1);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');

  const handleAccordionOpen = useCallback(() => {
    setPage(1);
    setExpandedItems(new Set());
    setSearch('');
  }, []);

  const { data: response, isLoading } = useQuery(itemsQuery(1000));
  const items = response?.data ?? [];

  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    const query = search.trim().toLowerCase();
    return items.filter((item) => item.name.toLowerCase().includes(query));
  }, [items, search]);

  const paginationMeta = useMemo(() => {
    const total = filteredItems.length;
    const lastPage = Math.max(1, Math.ceil(total / ITEMS_PER_PAGE));
    const currentPage = Math.min(page, lastPage);

    return {
      current_page: currentPage,
      last_page: lastPage,
      total,
      per_page: ITEMS_PER_PAGE,
    };
  }, [filteredItems.length, page]);

  useEffect(() => {
    if (paginationMeta.current_page !== page) {
      setPage(paginationMeta.current_page);
    }
  }, [paginationMeta.current_page, page]);

  const pagedItems = useMemo(() => {
    const start = (paginationMeta.current_page - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return filteredItems.slice(start, end);
  }, [filteredItems, paginationMeta.current_page]);

  const toggleExpanded = (itemId: number) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const customTable = (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead className="w-8" />
          <TableHead>Item</TableHead>
          <TableHead>Strength</TableHead>
          <TableHead>Price</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {pagedItems.map((item: Item) => {
          const strengths = item.strengths ?? [];
          const hasMultipleStrengths = strengths.length > 1;
          const isExpanded = expandedItems.has(item.id);
          const firstStrength = strengths[0];

          return (
            <Fragment key={item.id}>
              <TableRow
                className={cn(
                  hasMultipleStrengths && 'hover:bg-muted/50 cursor-pointer',
                )}
                onClick={() => hasMultipleStrengths && toggleExpanded(item.id)}
              >
                <TableCell className="w-8">
                  {hasMultipleStrengths && (
                    <ChevronDown
                      className={cn(
                        'size-4 transition-transform',
                        isExpanded && 'rotate-180',
                      )}
                    />
                  )}
                </TableCell>
                <TableCell>{item.name}</TableCell>
                <TableCell>
                  {hasMultipleStrengths
                    ? `${strengths.length} variants`
                    : getItemStrengthLabel(firstStrength)}
                </TableCell>
                <TableCell>
                  {hasMultipleStrengths ? '-' : formatPrice(firstStrength)}
                </TableCell>
              </TableRow>

              {isExpanded &&
                strengths.map((strength) => (
                  <TableRow
                    key={`${item.id}-${strength.id}`}
                    className="bg-slate-50"
                  >
                    <TableCell className="w-8" />
                    <TableCell className="pl-2">{item.name}</TableCell>
                    <TableCell>{getItemStrengthLabel(strength)}</TableCell>
                    <TableCell>{formatPrice(strength)}</TableCell>
                  </TableRow>
                ))}
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );

  const filtersComponent = (
    <div className="flex max-w-md items-center gap-4 pt-3">
      <Input
        label="Search item name"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(1);
        }}
      />
    </div>
  );

  return (
    <DashboardTableAccordion
      value="item-prices"
      title="Item prices"
      accordionOpenValue={accordionOpenValue}
      onOpen={handleAccordionOpen}
      data={pagedItems}
      isLoading={isLoading}
      emptyMessage="No items found."
      loadingMessage="Loading items..."
      paginationMeta={paginationMeta}
      onPageChange={setPage}
      filtersComponent={filtersComponent}
      customTable={customTable}
    />
  );
};
